import React from 'react';

const EFFECTIVE_DATE = 'April 20, 2026';

export default function PrivacyStatement() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>

        <div style={styles.eyebrow}>THE CORPORATE FIXERS</div>
        <div style={styles.title}>TCF AI Portal</div>
        <div style={styles.subtitle}>Data Privacy Statement</div>
        <div style={styles.date}>Effective Date: {EFFECTIVE_DATE}</div>

        <div style={styles.divider} />

        <Section>
          The Corporate Fixers (TCF) is committed to handling participant data responsibly,
          transparently, and with respect for the organizations and individuals we serve.
          This statement explains what data we collect through the TCF AI Portal, how it is used,
          who can access it, and how it is protected.
        </Section>

        <Heading>Who This Applies To</Heading>
        <Section>
          This statement applies to all individuals who access the TCF AI Portal as part of a
          corporate training engagement facilitated by The Corporate Fixers. This includes program
          participants, company administrators, and TCF staff.
        </Section>

        <Heading>What Data We Collect</Heading>
        <Section>When a participant registers and engages with the TCF AI Portal, the following data is collected:</Section>
        <Bullets items={[
          'Name and email address used to create the account',
          'Job title and department (as entered during registration)',
          'Company name and assigned engagement code',
          'Workbook responses submitted during training exercises',
          'Pre- and post-assessment scores',
          'Program completion status and certificate records',
          'Wins Wall posts, if the participant chooses to share them',
        ]} />
        <Section>
          We do not collect payment information, government-issued identification, health information,
          or any data unrelated to program participation.
        </Section>

        <Heading>How Data Is Used</Heading>
        <Section>Participant data is used exclusively for the following purposes:</Section>
        <Bullets items={[
          'Delivering and tracking progress through the training program',
          'Generating completion certificates upon program requirements being met',
          'Enabling company administrators to monitor engagement-level participation and completion',
          'Providing TCF facilitators with the context needed to support participants effectively',
        ]} />
        <Section>
          Participant data is never used to train AI models, sold to third parties, shared with
          advertisers, or used for any purpose outside the scope of the training engagement.
        </Section>

        <Heading>Who Can Access Your Data</Heading>
        <Section>Access to participant data is enforced at the database level through a four-tier permission structure:</Section>
        <Bullets items={[
          'Participants can only access their own records',
          'Company administrators can view completion data and assessment scores for participants within their assigned engagement only',
          'TCF administrators can access data only for companies explicitly assigned to them by TCF leadership',
          'Tamica Sears, as the primary administrator of The Corporate Fixers, has full access to all engagement data for program management purposes',
        ]} />
        <Section>
          No participant can access another participant's data. No company administrator can access
          data outside their assigned engagement.
        </Section>

        <Heading>How Data Is Protected</Heading>
        <Section>The TCF AI Portal is built on Google Firebase, a cloud infrastructure platform used by enterprise organizations worldwide. Data protections include:</Section>
        <Bullets items={[
          'All data is encrypted in transit using HTTPS and encrypted at rest within Google\'s infrastructure',
          'Authentication is handled by Firebase Authentication. Passwords are never stored in plain text',
          'Access control rules are enforced at the database level, not solely within the application interface',
          'Each client engagement is isolated by a unique company code. Cross-company data access is not permitted by the system architecture',
        ]} />

        <Heading>Data Retention and Deletion</Heading>
        <Section>
          The Corporate Fixers retains participant data for the duration of the engagement and for
          a reasonable period thereafter to support certificate verification and program records.
        </Section>
        <Section>
          When data deletion is requested, or when an engagement is closed and full removal is
          appropriate, the TCF AI Portal performs a complete deletion. This wipes the participant's
          profile, assessment responses, workbook entries, wins posts, certificates, prompt library,
          and any administrative records associated with their account. Both the database record
          (Firestore) and the login credential (Firebase Authentication) are removed. Once
          deletion is confirmed, the data cannot be recovered.
        </Section>
        <Section>
          To request deletion of your data or your organization's engagement data, contact{' '}
          <a href="mailto:tamica@thecorporatefixers.com" style={styles.link}>tamica@thecorporatefixers.com</a>.
          Confirmed requests are processed by the TCF primary administrator and complete immediately
          upon execution.
        </Section>
        <Section>
          For internal accountability, TCF maintains a deletion audit log capturing the date of
          deletion, who initiated it, and the count of records removed. This log does not contain
          any participant content or restoreable data.
        </Section>

        <Heading>Third-Party Services</Heading>
        <Section>The TCF AI Portal operates on the following third-party infrastructure:</Section>
        <Bullets items={[
          'Google Firebase: Authentication and database services',
          'Google Firestore: Encrypted cloud data storage',
          'Netlify: Application hosting and deployment',
        ]} />
        <Section>
          These platforms are governed by their own privacy and security policies. TCF does not
          share participant data with these providers beyond what is technically necessary to
          operate the portal.
        </Section>

        <Heading>Your Rights</Heading>
        <Section>Participants and client organizations have the right to:</Section>
        <Bullets items={[
          'Request a summary of data stored for their account or engagement',
          'Request correction of inaccurate information',
          'Request deletion of all data associated with their engagement',
        ]} />
        <Section>
          To exercise any of these rights, contact The Corporate Fixers directly at{' '}
          <a href="mailto:tamica@thecorporatefixers.com" style={styles.link}>tamica@thecorporatefixers.com</a>.
        </Section>

        <Heading>Questions and Contact</Heading>
        <Section>If you have questions about this statement or how your organization's data is handled, reach out directly.</Section>
        <div style={styles.contact}>
          <div style={styles.contactName}>Tamica Sears</div>
          <div style={styles.contactLine}>The Corporate Fixers</div>
          <div style={styles.contactLine}>
            <a href="mailto:tamica@thecorporatefixers.com" style={styles.link}>tamica@thecorporatefixers.com</a>
          </div>
        </div>

        <div style={styles.divider} />
        <div style={styles.footer}>
          This statement is reviewed and updated as needed. The effective date above reflects the most recent revision.
        </div>

      </div>
    </div>
  );
}

function Heading({ children }) {
  return <div style={styles.heading}>{children}</div>;
}

function Section({ children }) {
  return <p style={styles.body}>{children}</p>;
}

function Bullets({ items }) {
  return (
    <ul style={styles.list}>
      {items.map((item, i) => (
        <li key={i} style={styles.listItem}>{item}</li>
      ))}
    </ul>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FAF8F5',
    padding: '48px 24px',
  },
  container: {
    maxWidth: '720px',
    margin: '0 auto',
    background: '#FFFFFF',
    border: '1.5px solid #E8E6E2',
    padding: '48px 56px',
  },
  eyebrow: {
    fontSize: '11px',
    fontFamily: 'DM Sans, sans-serif',
    letterSpacing: '2px',
    color: '#8A8A8A',
    marginBottom: '8px',
    fontWeight: '600',
  },
  title: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '32px',
    fontWeight: '900',
    color: '#1B2A4A',
    marginBottom: '4px',
  },
  subtitle: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '18px',
    color: '#555555',
    fontWeight: '300',
    marginBottom: '8px',
  },
  date: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    color: '#8A8A8A',
    fontStyle: 'italic',
    marginBottom: '32px',
  },
  divider: {
    borderTop: '1.5px solid #E8E6E2',
    margin: '32px 0',
  },
  heading: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1B2A4A',
    marginTop: '32px',
    marginBottom: '10px',
  },
  body: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14px',
    color: '#3D3D3D',
    lineHeight: '1.8',
    fontWeight: '300',
    marginBottom: '12px',
  },
  list: {
    paddingLeft: '24px',
    marginBottom: '16px',
  },
  listItem: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14px',
    color: '#3D3D3D',
    lineHeight: '1.8',
    fontWeight: '300',
    marginBottom: '6px',
  },
  contact: {
    background: '#FAF8F5',
    border: '1.5px solid #E8E6E2',
    padding: '20px 24px',
    marginTop: '16px',
    marginBottom: '8px',
  },
  contactName: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14px',
    fontWeight: '700',
    color: '#1B2A4A',
    marginBottom: '4px',
  },
  contactLine: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14px',
    color: '#3D3D3D',
    fontWeight: '300',
    lineHeight: '1.8',
  },
  link: {
    color: '#1B2A4A',
    fontWeight: '600',
  },
  footer: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    color: '#8A8A8A',
    fontStyle: 'italic',
    lineHeight: '1.6',
  },
};
